/**
 * Smart Blog Generation Service
 * Generates weekly blog drafts linking heritage botanicals to current skin concerns
 * SEO-optimized with FAQ Schema and meta descriptions
 */

import { HERO_BOTANICALS, SA_CLIMATE_CONTEXTS } from './analysisEngine';

export interface BlogPost {
  id: string;
  title: string;
  slug: string;
  metaDescription: string;
  excerpt: string;
  content: string;
  category: string;
  tags: string[];
  heroIngredient?: string;
  skinConcern?: string;
  climateContext?: string;
  faqSchema: FAQItem[];
  internalLinks: InternalLink[];
  readingTime: number;
  publishDate: string;
  featuredImage?: string;
}

export interface FAQItem {
  question: string;
  answer: string;
}

export interface InternalLink {
  text: string;
  sku: string;
  url: string;
}

export interface BlogGenerationRequest {
  season: 'summer' | 'autumn' | 'winter' | 'spring';
  year: number;
  weekNumber: number;
  focusIngredient?: string;
  focusConcern?: string;
  focusClimate?: string;
}

// Seasonal context for South Africa
const SEASONAL_CONTEXT: Record<string, {
  name: string;
  months: string;
  skinChallenges: string[];
  botanicalFocus: string[];
  contentThemes: string[];
}> = {
  summer: {
    name: 'Summer',
    months: 'December - February',
    skinChallenges: [
      'Increased UV exposure and sun damage',
      'Excess oil production and shine',
      'Dehydration from heat',
      'Hyperpigmentation risks',
      'Heat rash and sensitivity'
    ],
    botanicalFocus: ['mongongo', 'kalahari_melon', 'rooibos'],
    contentThemes: [
      'Sun protection and repair',
      'Lightweight hydration',
      'Oil control without stripping',
      'Post-sun recovery routines'
    ]
  },
  autumn: {
    name: 'Autumn',
    months: 'March - May',
    skinChallenges: [
      'Transitional skin adjustments',
      'Repairing summer damage',
      'Uneven skin tone',
      'Beginning of dryness',
      'Barrier repair needs'
    ],
    botanicalFocus: ['marula', 'baobab', 'honeybush'],
    contentThemes: [
      'Repair and renewal',
      'Brightening treatments',
      'Preparing for winter',
      'Barrier strengthening'
    ]
  },
  winter: {
    name: 'Winter',
    months: 'June - August',
    skinChallenges: [
      'Severe dryness and dehydration',
      'Trans-epidermal water loss',
      'Sensitive and reactive skin',
      'Dullness and lack of radiance',
      'Cracked and flaky skin'
    ],
    botanicalFocus: ['baobab', 'marula', 'rooibos'],
    contentThemes: [
      'Deep hydration strategies',
      'Protective barrier care',
      'Rich nourishment',
      'Indoor heating protection'
    ]
  },
  spring: {
    name: 'Spring',
    months: 'September - November',
    skinChallenges: [
      'Allergies and sensitivities',
      'Uneven texture',
      'Dull winter skin revival',
      'Preparing for increased sun',
      'Congestion and breakouts'
    ],
    botanicalFocus: ['kalahari_melon', 'honeybush', 'rooibos'],
    contentThemes: [
      'Gentle exfoliation',
      'Radiance revival',
      'Allergy-safe routines',
      'Spring cleaning for skin'
    ]
  }
};

// Product catalog for internal linking
const PRODUCT_LINKS: Record<string, InternalLink[]> = {
  baobab: [
    { text: 'Baobab Nourishing Cleanser', sku: 'MAR-001', url: '/product/baobab-nourishing-cleanser' },
    { text: 'Baobab Deep Moisture Serum', sku: 'MAR-005', url: '/product/baobab-deep-moisture-serum' },
    { text: 'Baobab Intense Hydration Mask', sku: 'MAR-009', url: '/product/baobab-intense-hydration-mask' }
  ],
  kalahari_melon: [
    { text: 'Kalahari Balancing Cleanser', sku: 'MAR-002', url: '/product/kalahari-balancing-cleanser' },
    { text: 'Kalahari Light Hydration Serum', sku: 'MAR-004', url: '/product/kalahari-light-hydration-serum' }
  ],
  mongongo: [
    { text: 'Mongongo Protective Day Cream SPF 30', sku: 'MAR-006', url: '/product/mongongo-protective-day-cream' }
  ],
  marula: [
    { text: 'Marula Radiance Night Cream', sku: 'MAR-007', url: '/product/marula-radiance-night-cream' }
  ],
  rooibos: [
    { text: 'Rooibos Soothing Toner', sku: 'MAR-003', url: '/product/rooibos-soothing-toner' },
    { text: 'Rooibos Calming Mask', sku: 'MAR-010', url: '/product/rooibos-calming-mask' }
  ],
  honeybush: [
    { text: 'Honeybush Clarifying Treatment', sku: 'MAR-008', url: '/product/honeybush-clarifying-treatment' }
  ]
};

// Skin concern content templates
const SKIN_CONCERN_CONTENT: Record<string, {
  title: string;
  introduction: string;
  sections: string[];
  tips: string[];
  faqs: FAQItem[];
}> = {
  dehydration: {
    title: 'Combating Dehydration: The Hidden Cause of Premature Aging',
    introduction: `Dehydrated skin is often mistaken for dry skin, but the distinction is crucial. While dry skin lacks oil, dehydrated skin lacks water—a condition that can affect any skin type, including oily skin. In the harsh South African climate, understanding this difference is your first step to radiant, healthy skin.`,
    sections: [
      'Understanding the Difference: Dry vs. Dehydrated Skin',
      'Signs Your Skin is Dehydrated',
      'The Science of Transepidermal Water Loss (TEWL)',
      'African Botanical Solutions for Deep Hydration',
      'Building Your Hydration-First Routine'
    ],
    tips: [
      'Drink at least 2 liters of water daily',
      'Apply hydrating products to damp skin',
      'Use a humidifier in dry environments',
      'Avoid hot showers that strip natural moisture',
      'Layer lightweight hydrators before heavy creams'
    ],
    faqs: [
      {
        question: 'Can oily skin be dehydrated?',
        answer: 'Absolutely. Oily skin can lack water while overproducing oil. This is why some people experience both shine and tightness. The key is using lightweight, non-comedogenic hydrators like our Kalahari Light Hydration Serum.'
      },
      {
        question: 'How long does it take to fix dehydrated skin?',
        answer: 'With consistent care, you can see improvement in 2-3 days, with significant results in 2-4 weeks. The key is consistency and using the right ingredients.'
      },
      {
        question: 'What ingredients should I look for?',
        answer: 'Look for hyaluronic acid, glycerin, and natural humectants. Our Baobab Deep Moisture Serum combines these with the exceptional moisturizing properties of Baobab oil for lasting hydration.'
      }
    ]
  },
  sun_damage: {
    title: 'Repair and Protect: Your Guide to Sun-Damaged Skin',
    introduction: `South Africa\'s intense UV exposure makes sun protection non-negotiable. But what if the damage is already done? The good news: with the right approach and powerful African botanicals, you can repair existing damage and prevent future harm.`,
    sections: [
      'Understanding UV Damage: UVA vs UVB',
      'Signs of Sun Damage on Your Skin',
      'The Repair Process: What Science Says',
      'Mongongo Oil: Nature\'s UV Shield',
      'Your Repair and Protect Routine'
    ],
    tips: [
      'Wear SPF 50+ every single day, rain or shine',
      'Reapply sunscreen every 2 hours when outdoors',
      'Seek shade between 10 AM and 4 PM',
      'Wear protective clothing and wide-brimmed hats',
      'Use antioxidant-rich products to neutralize free radicals'
    ],
    faqs: [
      {
        question: 'Can you actually reverse sun damage?',
        answer: 'While you can\'t completely reverse deep damage, you can significantly improve texture, tone, and prevent further damage. Consistent use of antioxidants, gentle exfoliation, and strict sun protection can yield remarkable results.'
      },
      {
        question: 'What makes Mongongo Oil special for sun protection?',
        answer: 'Mongongo oil contains eleostearic acid, which polymerizes under UV light, creating a protective layer. Combined with its high vitamin E content, it offers natural protection while nourishing the skin.'
      },
      {
        question: 'How long does sun damage repair take?',
        answer: 'Surface improvements can be seen in 4-6 weeks with consistent care. Deeper damage may take 3-6 months to show significant improvement. Patience and consistency are key.'
      }
    ]
  },
  sensitivity: {
    title: 'Gentle Power: Managing Sensitive Skin with African Botanicals',
    introduction: `Sensitive skin requires a delicate balance—effective care without irritation. Traditional African botanicals have been soothing sensitive skin for generations. Discover how these time-tested ingredients can calm your skin and strengthen its natural defenses.`,
    sections: [
      'Understanding Skin Sensitivity: Causes and Triggers',
      'The African Approach to Sensitive Skin',
      'Rooibos: The Soothing Superpower',
      'Building a Minimal, Effective Routine',
      'Lifestyle Factors That Impact Sensitivity'
    ],
    tips: [
      'Patch test all new products for 24 hours',
      'Introduce one new product at a time',
      'Avoid fragrance and essential oils',
      'Use lukewarm water, never hot',
      'Protect your skin barrier with gentle, pH-balanced products'
    ],
    faqs: [
      {
        question: 'Can sensitive skin use active ingredients?',
        answer: 'Yes, but with caution. Start with gentle actives like niacinamide and low concentrations. Our Rooibos Soothing Toner prepares sensitive skin for treatments while providing antioxidant benefits.'
      },
      {
        question: 'What makes Rooibos good for sensitive skin?',
        answer: 'Rooibos contains aspalathin, a powerful antioxidant with anti-inflammatory properties. It\'s naturally caffeine-free and has been used traditionally to soothe skin irritations and redness.'
      },
      {
        question: 'How do I know if a product is right for sensitive skin?',
        answer: 'Look for fragrance-free formulas, gentle pH (around 5.5), and soothing ingredients. All Marple products are formulated with sensitive skin in mind, using minimal, effective ingredients.'
      }
    ]
  },
  aging: {
    title: 'Ageless Beauty: The African Approach to Graceful Aging',
    introduction: `Aging is a privilege, and your skincare should celebrate that journey. African botanicals offer some of nature\'s most powerful anti-aging compounds—antioxidants, fatty acids, and vitamins that work in harmony with your skin\'s natural processes.`,
    sections: [
      'Understanding the Aging Process',
      'Prevention vs. Correction: The Dual Approach',
      'Baobab: The Tree of Life for Your Skin',
      'Antioxidants: Your Anti-Aging Army',
      'Building Your Age-Defying Routine'
    ],
    tips: [
      'Start prevention in your mid-20s',
      'Never skip sunscreen—it\'s the best anti-aging product',
      'Get adequate sleep for cellular repair',
      'Manage stress—it accelerates aging',
      'Stay hydrated from the inside out'
    ],
    faqs: [
      {
        question: 'At what age should I start anti-aging skincare?',
        answer: 'Prevention should start in your mid-20s with sunscreen and antioxidants. Treatment products can be introduced in your 30s based on your skin\'s needs. It\'s never too early or too late to start caring for your skin.'
      },
      {
        question: 'What makes Baobab oil anti-aging?',
        answer: 'Baobab oil is exceptionally rich in vitamins A, D, E, and F. It improves skin elasticity, promotes cell renewal, and provides deep nourishment that helps minimize the appearance of fine lines.'
      },
      {
        question: 'Can natural ingredients really fight aging?',
        answer: 'Absolutely. Many synthetic anti-aging ingredients are derived from natural sources. African botanicals like Baobab and Marula contain concentrated antioxidants that neutralize free radicals—the primary cause of premature aging.'
      }
    ]
  },
  acne: {
    title: 'Clear Skin, Naturally: Managing Breakouts with African Wisdom',
    introduction: `Acne affects more than just teenagers—it can persist into adulthood and affect self-confidence. The African approach to clear skin combines gentle yet effective botanicals that address the root causes without harsh chemicals that damage your skin barrier.`,
    sections: [
      'Understanding Acne: Beyond Surface Level',
      'The Role of Inflammation in Breakouts',
      'Honeybush: Nature\'s Clarifying Agent',
      'Balancing Oil Production Naturally',
      'Your Clear Skin Action Plan'
    ],
    tips: [
      'Don\'t over-cleanse—twice daily is enough',
      'Avoid picking or squeezing blemishes',
      'Change pillowcases twice weekly',
      'Keep hair products away from your face',
      'Manage stress through exercise or meditation'
    ],
    faqs: [
      {
        question: 'Will oil-based products make my acne worse?',
        answer: 'Not if you choose the right oils. Non-comedogenic oils like Kalahari Melon Seed Oil can actually help balance oil production. The key is using lightweight, properly formulated products.'
      },
      {
        question: 'How does Honeybush help with acne?',
        answer: 'Honeybush has natural antimicrobial properties that help combat acne-causing bacteria. It also reduces inflammation and promotes healing, addressing both active breakouts and post-acne marks.'
      },
      {
        question: 'How long before I see results?',
        answer: 'Consistency is key. You may see initial improvement in 2-4 weeks, with significant results in 8-12 weeks. Stick with your routine and resist the urge to switch products too frequently.'
      }
    ]
  }
};

// Routine spotlight content
const ROUTINE_SPOTLIGHTS: Record<string, {
  title: string;
  introduction: string;
  steps: Array<{
    name: string;
    product: string;
    instruction: string;
    benefit: string;
  }>;
  results: string;
  faqs: FAQItem[];
}> = {
  '3-step-glow': {
    title: 'The 3-Step Glow Kit: Your Path to Radiant Skin',
    introduction: `Sometimes, less is more. Our 3-Step Glow Kit distills the essence of African botanical wisdom into a simple yet powerful routine. Designed for busy lives but delivering spa-quality results, this kit focuses on the fundamentals: cleanse, treat, and protect.`,
    steps: [
      {
        name: 'Step 1: Cleanse',
        product: 'Baobab Nourishing Cleanser',
        instruction: 'Massage onto damp skin for 60 seconds, then rinse with lukewarm water.',
        benefit: 'Removes impurities while maintaining your skin\'s moisture barrier'
      },
      {
        name: 'Step 2: Treat',
        product: 'Marula Radiance Serum',
        instruction: 'Apply 2-3 drops to face and neck, pressing gently into skin.',
        benefit: 'Delivers concentrated antioxidants for a natural glow'
      },
      {
        name: 'Step 3: Protect',
        product: 'Mongongo Protective Day Cream SPF 30',
        instruction: 'Apply generously as the final step of your morning routine.',
        benefit: 'Shields from UV damage while providing all-day hydration'
      }
    ],
    results: 'With consistent use, expect to see improved radiance within 2 weeks, with significant glow and skin health improvements by week 4.',
    faqs: [
      {
        question: 'Can I use this routine if I have sensitive skin?',
        answer: 'Absolutely. The 3-Step Glow Kit is formulated with gentle, effective ingredients suitable for all skin types, including sensitive skin. Each product is fragrance-free and pH-balanced.'
      },
      {
        question: 'How long will the products last?',
        answer: 'With twice-daily use, the full-size kit typically lasts 2-3 months. A little goes a long way with our concentrated formulas.'
      },
      {
        question: 'Can I add additional steps to this routine?',
        answer: 'Yes! This routine forms an excellent foundation. You can add treatments like the Honeybush Clarifying Treatment or Baobab Intense Hydration Mask based on your skin\'s needs.'
      }
    ]
  },
  'hydration-heroes': {
    title: 'Hydration Heroes: The Ultimate Dry Skin Rescue',
    introduction: `When your skin is crying out for moisture, you need more than just a basic moisturizer. Our Hydration Heroes routine layers multiple hydration sources for deep, lasting relief. Perfect for winter months or anyone battling persistent dryness.`,
    steps: [
      {
        name: 'Step 1: Gentle Cleanse',
        product: 'Baobab Nourishing Cleanser',
        instruction: 'Use lukewarm water and gentle circular motions. Pat dry, don\'t rub.',
        benefit: 'Cleanses without stripping essential moisture'
      },
      {
        name: 'Step 2: Deep Hydration',
        product: 'Baobab Deep Moisture Serum',
        instruction: 'Apply to slightly damp skin to lock in hydration.',
        benefit: 'Hyaluronic acid and Baobab oil deliver intensive moisture'
      },
      {
        name: 'Step 3: Seal & Protect',
        product: 'Marula Radiance Night Cream',
        instruction: 'Apply generously, focusing on extra-dry areas.',
        benefit: 'Creates a protective barrier to prevent moisture loss overnight'
      }
    ],
    results: 'Experience immediate relief from tightness, with skin feeling plump and hydrated by morning. Continued use restores your skin\'s natural moisture balance.',
    faqs: [
      {
        question: 'Can I use this routine in the morning too?',
        answer: 'Yes, though we recommend using the night cream in your PM routine and switching to Mongongo Protective Day Cream SPF 30 for daytime to ensure sun protection.'
      },
      {
        question: 'Will this routine be too heavy for combination skin?',
        answer: 'The products are designed to absorb fully without feeling greasy. For combination skin, use a lighter application in your T-zone and focus richer application on dry areas.'
      },
      {
        question: 'How quickly will I see results?',
        answer: 'Most users report immediate relief from dryness and tightness. Significant improvement in skin texture and hydration levels typically occurs within 1-2 weeks of consistent use.'
      }
    ]
  },
  'clarity-collection': {
    title: 'The Clarity Collection: Clear Skin Without Compromise',
    introduction: `Achieving clear skin shouldn\'t mean stripping your skin barrier or dealing with irritation. The Clarity Collection uses the antimicrobial power of Honeybush and the balancing properties of Kalahari Melon to address breakouts while maintaining healthy skin.`,
    steps: [
      {
        name: 'Step 1: Balance Cleanse',
        product: 'Kalahari Balancing Cleanser',
        instruction: 'Massage into damp skin, focusing on congested areas.',
        benefit: 'Removes excess oil without disrupting your skin barrier'
      },
      {
        name: 'Step 2: Target Treatment',
        product: 'Honeybush Clarifying Treatment',
        instruction: 'Apply to blemishes and congested areas only. Use 3x weekly.',
        benefit: 'Natural antimicrobials target acne-causing bacteria'
      },
      {
        name: 'Step 3: Lightweight Hydrate',
        product: 'Kalahari Light Hydration Serum',
        instruction: 'Apply 2-3 drops all over face for balanced hydration.',
        benefit: 'Hydrates without clogging pores or adding shine'
      }
    ],
    results: 'Expect reduced inflammation within days, with fewer new breakouts appearing within 2-3 weeks. Post-acne marks fade over 4-6 weeks with consistent use.',
    faqs: [
      {
        question: 'Can I use this with prescription acne medication?',
        answer: 'Consult your dermatologist before combining with prescription treatments. Our products are gentle, but it\'s important to ensure compatibility with your current regimen.'
      },
      {
        question: 'Will this dry out my skin?',
        answer: 'No. Unlike harsh acne treatments, our Clarity Collection maintains your skin\'s moisture balance while addressing breakouts. The Kalahari Light Hydration Serum ensures adequate hydration.'
      },
      {
        question: 'Is this suitable for adult acne?',
        answer: 'Yes, this routine is designed for all ages. Adult acne often requires a gentler approach that respects the skin barrier—exactly what the Clarity Collection provides.'
      }
    ]
  }
};

/**
 * Generate blog post about a hero ingredient
 */
function generateIngredientPost(
  ingredientKey: string,
  season: string,
  weekNumber: number
): BlogPost {
  const ingredient = HERO_BOTANICALS[ingredientKey as keyof typeof HERO_BOTANICALS];
  const seasonContext = SEASONAL_CONTEXT[season];
  const id = `blog-${season}-w${weekNumber}-ingredient`;
  
  const content = `# The Science of ${ingredient.name}

## Introduction

${ingredient.origin}, ${ingredient.name.toLowerCase()} has been treasured for generations as a skincare secret. Today, modern science validates what traditional healers have known all along—this remarkable ingredient offers transformative benefits for your skin.

## The Origins

${ingredient.origin}, the source of ${ingredient.name.split(' ')[0]} has adapted to thrive in harsh conditions. These same protective compounds that help the plant survive translate into powerful benefits for your skin.

## The Science Behind the Benefits

${ingredient.benefits.map((benefit, i) => `${i + 1}. **${benefit}**`).join('\n\n')}

## Why It Matters for ${seasonContext.name} Skin

${seasonContext.skinChallenges.map(challenge => `- ${challenge}`).join('\n')}

During ${seasonContext.name.toLowerCase()}, your skin faces unique challenges. ${ingredient.name.split(' ')[0]} addresses these concerns through its unique composition of fatty acids, vitamins, and antioxidants.

## How to Incorporate It Into Your Routine

${PRODUCT_LINKS[ingredientKey]?.map(link => `- **[${link.text}](${link.url})**: ${link.sku}`).join('\n') || 'Products featuring this ingredient coming soon.'}

## The Marple Difference

At Marple & Co, we source our ${ingredient.name.split(' ')[0]} directly from sustainable farms across Africa. Our cold-press extraction method preserves every beneficial compound, ensuring you receive the full potency of this remarkable ingredient.

## Conclusion

${ingredient.name} represents the perfect marriage of traditional wisdom and modern skincare science. By incorporating this African treasure into your routine, you're not just caring for your skin—you're connecting with centuries of botanical heritage.

*Ready to experience the benefits? Explore our ${ingredient.name}-infused products and discover why this ingredient has been revered for generations.*`;

  const faqSchema: FAQItem[] = [
    {
      question: `What makes ${ingredient.name} different from other oils?`,
      answer: `${ingredient.name} is uniquely suited to ${ingredient.bestFor.join(', ')} skin types. Its composition of ${ingredient.benefits[0].toLowerCase()} makes it exceptionally effective.`
    },
    {
      question: 'Is it suitable for sensitive skin?',
      answer: 'Yes, our carefully formulated products are designed to be gentle on all skin types, including sensitive skin. We recommend patch testing any new product.'
    },
    {
      question: 'How long before I see results?',
      answer: 'Most users notice improved skin texture within 1-2 weeks, with significant results visible after 4-6 weeks of consistent use.'
    }
  ];

  return {
    id,
    title: `The Science of ${ingredient.name}: Africa's Skincare Secret`,
    slug: `science-of-${ingredientKey.replace('_', '-')}-african-skincare`,
    metaDescription: `Discover the science behind ${ingredient.name}, ${ingredient.origin.toLowerCase()}. Learn how this African botanical can transform your skincare routine.`,
    excerpt: `Explore the remarkable benefits of ${ingredient.name}, ${ingredient.origin.toLowerCase()}.`,
    content,
    category: 'Ingredients',
    tags: [ingredientKey, 'african-botanicals', 'skincare-science', season, 'natural-ingredients'],
    heroIngredient: ingredientKey,
    faqSchema,
    internalLinks: PRODUCT_LINKS[ingredientKey] || [],
    readingTime: 5,
    publishDate: new Date().toISOString().split('T')[0],
    featuredImage: `/blog/${ingredientKey}-science.jpg`
  };
}

/**
 * Generate blog post about managing a skin concern in current climate
 */
function generateConcernPost(
  concern: string,
  climate: string,
  season: string,
  weekNumber: number
): BlogPost {
  const concernData = SKIN_CONCERN_CONTENT[concern] || SKIN_CONCERN_CONTENT.dehydration;
  const climateContext = SA_CLIMATE_CONTEXTS[climate as keyof typeof SA_CLIMATE_CONTEXTS] || SA_CLIMATE_CONTEXTS.highveld;
  const seasonContext = SEASONAL_CONTEXT[season];
  const id = `blog-${season}-w${weekNumber}-concern`;
  
  // Select relevant products based on concern
  let relevantProducts: InternalLink[] = [];
  if (concern === 'dehydration') {
    relevantProducts = [...(PRODUCT_LINKS.baobab || []), ...(PRODUCT_LINKS.kalahari_melon || [])];
  } else if (concern === 'sun_damage') {
    relevantProducts = PRODUCT_LINKS.mongongo || [];
  } else if (concern === 'sensitivity') {
    relevantProducts = PRODUCT_LINKS.rooibos || [];
  } else if (concern === 'aging') {
    relevantProducts = [...(PRODUCT_LINKS.baobab || []), ...(PRODUCT_LINKS.marula || [])];
  } else if (concern === 'acne') {
    relevantProducts = [...(PRODUCT_LINKS.kalahari_melon || []), ...(PRODUCT_LINKS.honeybush || [])];
  }
  
  const content = `# ${concernData.title}

## Introduction

${concernData.introduction}

## Understanding ${climateContext.name}

Living in ${climateContext.name} presents unique challenges for your skin:

${climateContext.skinChallenges.map(challenge => `- ${challenge}`).join('\n')}

${seasonContext.contentThemes.map(theme => `During ${seasonContext.name.toLowerCase()}, ${theme.toLowerCase()} becomes especially important.`).join(' ')}

${concernData.sections.map(section => `## ${section}\n\n[Detailed content about ${section.toLowerCase()}]`).join('\n\n')}

## Expert Tips for ${climateContext.name}

${concernData.tips.map(tip => `- ${tip}`).join('\n')}

## Recommended Products

${relevantProducts.map(link => `- **[${link.text}](${link.url})** (${link.sku})`).join('\n')}

## Conclusion

Managing ${concern.replace('_', ' ')} in ${climateContext.name} requires understanding your environment and choosing the right ingredients. With consistent care and the power of African botanicals, you can achieve healthy, radiant skin year-round.

*Have questions about your specific skin concerns? Try our AI Skin Analysis for personalized recommendations.*`;

  return {
    id,
    title: `${concernData.title} in ${climateContext.name}`,
    slug: `manage-${concern.replace('_', '-')}-${climate}-south-africa`,
    metaDescription: `Learn how to manage ${concern.replace('_', ' ')} in ${climateContext.name}. Expert tips and African botanical solutions for South African climates.`,
    excerpt: `Expert guide to managing ${concern.replace('_', ' ')} in ${climateContext.name.toLowerCase()}.`,
    content,
    category: 'Skin Concerns',
    tags: [concern, climate, season, 'south-africa', 'skincare-tips'],
    skinConcern: concern,
    climateContext: climate,
    faqSchema: concernData.faqs,
    internalLinks: relevantProducts,
    readingTime: 6,
    publishDate: new Date().toISOString().split('T')[0],
    featuredImage: `/blog/${concern}-${climate}.jpg`
  };
}

/**
 * Generate routine spotlight blog post
 */
function generateRoutinePost(
  routineKey: string,
  season: string,
  weekNumber: number
): BlogPost {
  const routine = ROUTINE_SPOTLIGHTS[routineKey];
  const id = `blog-${season}-w${weekNumber}-routine`;
  
  const content = `# ${routine.title}

## Introduction

${routine.introduction}

## The Routine

${routine.steps.map(step => `### ${step.name}: ${step.product}\n\n**How to use:** ${step.instruction}\n\n**The benefit:** ${step.benefit}\n\n---`).join('\n\n')}

## What to Expect

${routine.results}

## Why This Works

This routine is built on three principles:

1. **Simplicity**: Fewer products mean less chance of irritation and better compliance
2. **Synergy**: Each product enhances the others for maximum benefit
3. **Sustainability**: African botanicals provide lasting results without harsh chemicals

## Customization Tips

- **For extra dry skin**: Add the Baobab Intense Hydration Mask twice weekly
- **For oily skin**: Use a lighter application of the night cream
- **For sensitive skin**: All products are fragrance-free and gentle, but patch test if you're extra sensitive

## The Complete Collection

${routine.steps.map(step => `- **${step.product}**: [Shop now](/shop)`).join('\n')}

## Conclusion

Great skin doesn't require a complicated routine. The ${routine.title.split(':')[0]} proves that with the right ingredients and consistent use, you can achieve remarkable results. Start your journey to radiant skin today.

*Ready to transform your routine? Shop the complete collection or take our Skin Analysis for personalized recommendations.*`;

  const internalLinks: InternalLink[] = [];
  routine.steps.forEach(step => {
    // Find matching product link
    Object.values(PRODUCT_LINKS).flat().forEach(link => {
      if (step.product.includes(link.text.split(' ')[0])) {
        internalLinks.push(link);
      }
    });
  });

  return {
    id,
    title: routine.title,
    slug: routineKey.replace(/-/g, '-') + '-routine',
    metaDescription: `Discover the ${routine.title.split(':')[0]}. A simple, effective skincare routine featuring African botanicals for visible results.`,
    excerpt: `A simple yet powerful routine featuring African botanicals.`,
    content,
    category: 'Routines',
    tags: ['skincare-routine', 'african-botanicals', 'simple-skincare', season, 'product-guide'],
    faqSchema: routine.faqs,
    internalLinks,
    readingTime: 4,
    publishDate: new Date().toISOString().split('T')[0],
    featuredImage: `/blog/${routineKey}-routine.jpg`
  };
}

/**
 * Generate FAQ Schema JSON-LD
 */
export function generateFAQSchema(faqs: FAQItem[]): string {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
  return JSON.stringify(schema, null, 2);
}

/**
 * Generate all three weekly blog posts
 */
export function generateWeeklyBlogPosts(request: BlogGenerationRequest): BlogPost[] {
  const posts: BlogPost[] = [];
  
  // 1. Hero Ingredient Science Post
  const ingredientKey = request.focusIngredient || 
    SEASONAL_CONTEXT[request.season].botanicalFocus[request.weekNumber % 3];
  posts.push(generateIngredientPost(ingredientKey, request.season, request.weekNumber));
  
  // 2. Skin Concern in Climate Post
  // Parenthesised: previously `focusConcern || <test> ? ... : ...` bound as
  // `(focusConcern || <test>) ? 'dehydration' : ...`, so any caller-supplied
  // focusConcern was ignored and always produced a dehydration post.
  const concern = request.focusConcern || (
    SEASONAL_CONTEXT[request.season].skinChallenges[request.weekNumber % 5].toLowerCase().includes('dry') ? 'dehydration' :
    SEASONAL_CONTEXT[request.season].skinChallenges[request.weekNumber % 5].toLowerCase().includes('sun') ? 'sun_damage' :
    SEASONAL_CONTEXT[request.season].skinChallenges[request.weekNumber % 5].toLowerCase().includes('sensitive') ? 'sensitivity' :
    'dehydration');
  const climate = request.focusClimate || 'highveld';
  posts.push(generateConcernPost(concern, climate, request.season, request.weekNumber));
  
  // 3. Routine Spotlight Post
  const routineKeys = Object.keys(ROUTINE_SPOTLIGHTS);
  const routineKey = routineKeys[request.weekNumber % routineKeys.length] as keyof typeof ROUTINE_SPOTLIGHTS;
  posts.push(generateRoutinePost(routineKey, request.season, request.weekNumber));
  
  return posts;
}

/**
 * Get current season based on month
 */
export function getCurrentSeason(month: number = new Date().getMonth()): 'summer' | 'autumn' | 'winter' | 'spring' {
  // Southern Hemisphere seasons
  if (month >= 11 || month <= 1) return 'summer';      // Dec-Feb
  if (month >= 2 && month <= 4) return 'autumn';       // Mar-May
  if (month >= 5 && month <= 7) return 'winter';       // Jun-Aug
  return 'spring';                                      // Sep-Nov
}

/**
 * Get current week number
 */
export function getWeekNumber(date: Date = new Date()): number {
  const start = new Date(date.getFullYear(), 0, 1);
  const diff = date.getTime() - start.getTime();
  const oneWeek = 1000 * 60 * 60 * 24 * 7;
  return Math.floor(diff / oneWeek) + 1;
}

/**
 * Generate blog posts for current week
 */
export function generateCurrentWeekPosts(): BlogPost[] {
  const now = new Date();
  return generateWeeklyBlogPosts({
    season: getCurrentSeason(now.getMonth()),
    year: now.getFullYear(),
    weekNumber: getWeekNumber(now)
  });
}

/**
 * Export blog post as markdown
 */
export function exportAsMarkdown(post: BlogPost): string {
  return `---
title: "${post.title}"
slug: "${post.slug}"
description: "${post.metaDescription}"
category: "${post.category}"
tags: [${post.tags.map(t => `"${t}"`).join(', ')}]
publishDate: "${post.publishDate}"
readingTime: ${post.readingTime}
featuredImage: "${post.featuredImage}"
---

${post.content}

## Frequently Asked Questions

${post.faqSchema.map((faq, i) => `### Q${i + 1}: ${faq.question}\n\n${faq.answer}`).join('\n\n')}

<!-- FAQ Schema -->
<script type="application/ld+json">
${generateFAQSchema(post.faqSchema)}
</script>
`;
}

export default {
  generateWeeklyBlogPosts,
  generateCurrentWeekPosts,
  generateFAQSchema,
  exportAsMarkdown,
  getCurrentSeason,
  getWeekNumber,
  SEASONAL_CONTEXT,
  HERO_BOTANICALS,
  SKIN_CONCERN_CONTENT,
  ROUTINE_SPOTLIGHTS
};
